import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { LoadingCrossfade } from '../../../components/loading-skeleton/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { SHEET_CLOSE_ANIMATION_MS } from '../../../components/sheet/index';
import { QueryKey } from '../../../constants/index';
import { useCompanyContextReady } from '../../../hooks/company/use-company-context-ready.hook';
import { PendingMutationSettleOutcome } from '../../../hooks/event/pending-events.store';
import {
  AnalyticHooks,
  Company,
  EventHooks,
  useLocale,
  useModalState,
  useQueryParams,
} from '../../../hooks/index';
import { EventTypeBackend, EventUnion } from '../../../types/events.types';
import { PossibleAction } from '../../../types/google-analytics-types.types';
import { objectTypeCheck } from '../../../utils/object-type-check.util';
import { ActivityOverview } from '../activity-overview';
import { EventsPagePlaceholder } from '../components/events-loading-placeholders';
import { eventsModalTypeComponentMap } from '../constant/events-modal-type-map.constant';
import { AddEventForm } from '../event-form/add/index';
import { EditEventModal } from '../event-form/edit/index';
import { EditEventStepVariant } from '../event-form/event-form.types';
import { useEventsSecondaryQueriesEnabled } from '../hooks/use-events-secondary-queries-enabled.hook';
import { ManageReverseVestingModal } from '../manage-reverse-vesting/index';
import { eventsModalStore } from '../store/events-modal.store';
import { buildTransactionDeletePayload } from '../utils/build-transaction-delete-payload.util';
import { eventHasTerminatedIssuanceStakeholder } from '../utils/terminated-stakeholder.util';
import { eventModalStore } from './desktop.store';
import { GraphsDesk } from './graphs-desk/index';
import { History } from './history/index';

type EventsDesktopProps = {
  companyId: string;
};

export type SelectedEvent = {
  id: string;
  step: EditEventStepVariant;
  transactionId?: string;
} & (
  | {
      defaultData?: {
        isOpen?: boolean;
      };
      mode: 'EDIT';
    }
  | {
      mode: 'EDIT' | 'VIEW';
    }
);

export const EventsDesktop: FC<EventsDesktopProps> = ({ companyId }) => {
  const { get } = useQueryParams();
  const { messagesLocale } = useLocale();
  const [selectedEvent, setSelectedEvent] = useState<null | SelectedEvent>({
    id: '',
    step: 1,
    mode: 'EDIT',
  });

  const isOpenAddEventModal = eventModalStore(({ isOpen }) => isOpen);
  const addEventModalToggler = eventModalStore(({ setOpenState }) => setOpenState);
  const eventsModalType = eventsModalStore(({ type }) => type);

  const handleOpenAddEventModal = () => {
    addEventModalToggler({ state: true });
  };

  const companyDeleteEventTracker = AnalyticHooks.useClick(PossibleAction.DELETE_EVENT);
  const { deleteEvent } = EventHooks.useDelete();

  const companyEditEventTracker = AnalyticHooks.useClick(PossibleAction.EDIT_EVENT);
  const { update } = EventHooks.useUpdate();
  const { fetchEvent } = EventHooks.useFetchEvent();

  const editModalCloseTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  const handleEditModalClose = useCallback(() => {
    if (editModalCloseTimeoutRef.current) {
      clearTimeout(editModalCloseTimeoutRef.current);
    }

    editModalCloseTimeoutRef.current = setTimeout(() => {
      setSelectedEvent(null);
      editModalCloseTimeoutRef.current = null;
    }, SHEET_CLOSE_ANIMATION_MS);
  }, []);

  const {
    isOpen: isOpenEditEventModal,
    handleOpenModal: handleOpenEditEventModal,
    handleCloseModal: handleCloseEditEventModal,
  } = useModalState('openEditModal', {
    onClose: handleEditModalClose,
  });

  useEffect(() => {
    if (isOpenEditEventModal && editModalCloseTimeoutRef.current) {
      clearTimeout(editModalCloseTimeoutRef.current);
      editModalCloseTimeoutRef.current = null;
    }
  }, [isOpenEditEventModal]);

  useEffect(
    () => () => {
      if (editModalCloseTimeoutRef.current) {
        clearTimeout(editModalCloseTimeoutRef.current);
      }
    },
    [],
  );

  const { issuedShares } = Company.useIssuedShares({
    companyId,
    enabled: isOpenAddEventModal || isOpenEditEventModal,
  });

  const secondaryQueriesEnabled = useEventsSecondaryQueriesEnabled(companyId);

  // Initial full-page skeleton until the company context is ready. It deliberately does NOT wait
  // on a snapshot: this page renders a timeline, and the cap-table snapshot it used to gate on was
  // never rendered here — only its isLoading was read, so every visit paid for a snapshot
  // recompute (1–3s uncached on a heavy company) before any chrome appeared. Each section below
  // owns its own skeleton, and the graph's queries wait for the events list via
  // `secondaryQueriesEnabled`.
  const isCompanyContextReady = useCompanyContextReady(companyId);

  const queryClient = useQueryClient();

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_ISSUED_SHARES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SNAPSHOTS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SNAPSHOT] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_APPROVE_SHARE_PLANS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SHARE_PLAN_VESTING_TASKS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VESTING_GRAPH] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANY_INCORPORATION_CONTEXT] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANY_GET_STARTED_PROGRESS] });
    // SAFE deletes ride the same event mutation flow, so the SAFEs page data must refetch too.
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SAFES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SAFE_CONVERSION] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SAFE_OVERALL_STATUS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SAFE_RECAP] });
  }, [queryClient]);

  const handleDeleteTransaction = useCallback(
    async (listEvent: EventUnion, transactionId: string) => {
      // The timeline list loads items without their stakeholder/share-class relations, so the edit
      // payload is rebuilt from the detail response instead of the row the user clicked.
      const event = await fetchEvent({ companyId, eventId: listEvent.eventId }).catch(() => null);

      if (!event) {
        toast.error(messagesLocale['UNABLE_TO_DELETE_TRANSACTION']);
        return;
      }

      update(
        {
          companyId,
          eventId: event.eventId,
          data: buildTransactionDeletePayload(event, transactionId),
          // Settle-time toast: the row wears "Editing…" until the queued apply lands.
          onApplySettled: (outcome) => {
            if (outcome === PendingMutationSettleOutcome.APPLIED) {
              toast.success(messagesLocale['TRANSACTION_SUCCESSFULLY_DELETED']);
            }
            if (outcome === PendingMutationSettleOutcome.FAILED) {
              toast.error(messagesLocale['UNABLE_TO_DELETE_TRANSACTION']);
            }
          },
        },
        {
          onSuccess: () => {
            companyEditEventTracker();
            invalidateQuery();
          },
        },
      );
    },
    [companyEditEventTracker, companyId, fetchEvent, invalidateQuery, messagesLocale, update],
  );

  const openEditModal = get('openEditModal');
  const selectedEventIdFromQuery = get('selectedEventId');

  useEffect(() => {
    const isOpen = !!openEditModal;

    if (isOpen && selectedEventIdFromQuery) {
      setSelectedEvent({ id: selectedEventIdFromQuery, mode: 'EDIT', step: 2 });
      handleOpenEditEventModal();
    } else {
      handleCloseEditEventModal();
    }
  }, [
    handleCloseEditEventModal,
    handleOpenEditEventModal,
    openEditModal,
    selectedEventIdFromQuery,
  ]);

  return (
    <>
      <ModalWrapper
        className="w-full max-w-[500px]"
        control={{
          open: isOpenAddEventModal,
          onOpenChange: (state) => addEventModalToggler({ state }),
        }}
        side="RIGHT"
      >
        <AddEventForm
          companyId={companyId}
          handleClose={() => addEventModalToggler({ state: false })}
          invalidateQuery={invalidateQuery}
          issuedShares={issuedShares || 0}
        />
      </ModalWrapper>

      {eventsModalTypeComponentMap[eventsModalType]}
      <ManageReverseVestingModal companyId={companyId} invalidateQuery={invalidateQuery} />
      <EditEventModal
        companyId={companyId}
        defaultData={
          selectedEvent &&
          objectTypeCheck<{
            defaultData?: {
              isOpen?: boolean;
            };
          }>(selectedEvent, 'defaultData')
            ? selectedEvent.defaultData
            : {}
        }
        eventId={selectedEvent?.id || ''}
        handleClose={handleCloseEditEventModal}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenEditEventModal}
        issuedShares={issuedShares || 0}
        onEditClick={({ id, step }) => {
          setSelectedEvent((prev) => {
            if (prev?.mode === 'VIEW' && prev.id === id) {
              return { ...prev, step };
            }
            return { id, mode: 'EDIT', step };
          });
        }}
        previewMode={selectedEvent?.mode === 'VIEW' || undefined}
        step={selectedEvent?.step || 1}
        transactionId={selectedEvent?.transactionId}
      />
      <LoadingCrossfade isLoading={!isCompanyContextReady} skeleton={<EventsPagePlaceholder />}>
        {() => (
          <div className="flex h-fit w-full flex-col gap-4 overflow-hidden p-[1px]">
            <ActivityOverview companyId={companyId} onAddEvent={handleOpenAddEventModal} />
            <GraphsDesk companyId={companyId} secondaryQueriesEnabled={secondaryQueriesEnabled} />
            <History
              companyId={companyId}
              onCloseRound={(id) => {
                handleOpenEditEventModal();
                setSelectedEvent({
                  id,
                  mode: 'EDIT',
                  step: 1,
                  defaultData: { isOpen: false },
                });
              }}
              onDelete={async (id, eventType) => {
                // Issuance-bearing events settle a terminated stakeholder's holdings, so their
                // deletion is blocked. The timeline row has no stakeholder relations — the check
                // needs the detail response (same shape handleDeleteTransaction rebuilds from).
                if (
                  eventType === EventTypeBackend.SHARE_ISSUANCE ||
                  eventType === EventTypeBackend.COMPANY_INCORPORATION
                ) {
                  const detailEvent = await fetchEvent({ companyId, eventId: id }).catch(
                    () => null,
                  );

                  // A failed fetch is a transient network/server problem, not the linkage rule
                  // UNABLE_TO_DELETE_EVENT describes — saying so would send the operator looking
                  // for a connected event that does not exist.
                  if (!detailEvent) {
                    toast.error(messagesLocale['UNEXPECTED_ERROR_OCCURRED']);
                    return;
                  }

                  if (eventHasTerminatedIssuanceStakeholder(detailEvent)) {
                    toast.error(messagesLocale['EVENT_DELETE_TERMINATED_STAKEHOLDER_BLOCKED']);
                    return;
                  }
                }

                deleteEvent(
                  {
                    companyId,
                    eventId: id,
                    // The toast waits for the queued apply to settle (row leaves "Deleting…"),
                    // so it agrees with the list instead of firing mid-pending.
                    onApplySettled: (outcome) => {
                      if (outcome === PendingMutationSettleOutcome.APPLIED) {
                        toast.success(
                          eventType === EventTypeBackend.COMPANY_INCORPORATION
                            ? messagesLocale['COMPANY_INCORPORATION_EVENT_SUCCESSFULLY_DELETED']
                            : messagesLocale['EVENT_SUCCESSFULLY_DELETED'],
                        );
                      }
                      if (outcome === PendingMutationSettleOutcome.FAILED) {
                        toast.error(messagesLocale['EVENT_DELETE_FAILED']);
                      }
                    },
                  },
                  {
                    onSuccess: () => {
                      companyDeleteEventTracker();
                      invalidateQuery();
                    },
                    onError: (error) => {
                      if (error.body?.message === 'Internal server error') {
                        toast.error(messagesLocale['UNABLE_TO_DELETE_EVENT']);
                      } else {
                        toast.error(error.body?.message);
                      }
                    },
                  },
                );
              }}
              onEdit={(id, step = 4) => {
                handleOpenEditEventModal();
                setSelectedEvent({
                  id,
                  mode: 'EDIT',
                  step,
                });
              }}
              onTransactionDelete={(event, transactionId) => {
                void handleDeleteTransaction(event, transactionId);
              }}
              onTransactionEdit={(id, transactionId, previewStep = 4) => {
                handleOpenEditEventModal();
                setSelectedEvent({
                  id,
                  mode: 'EDIT',
                  step: previewStep,
                  transactionId,
                });
              }}
              onTransactionView={(id, transactionId, previewStep = 4) => {
                handleOpenEditEventModal();
                setSelectedEvent({
                  id,
                  mode: 'VIEW',
                  step: previewStep,
                  transactionId,
                });
              }}
              onView={(id, previewStep = 4) => {
                handleOpenEditEventModal();
                setSelectedEvent({
                  id,
                  mode: 'VIEW',
                  step: previewStep,
                });
              }}
            />
          </div>
        )}
      </LoadingCrossfade>
    </>
  );
};
